/**
 * useMediaRequests — reads the normalized media request list.
 *
 * Types here mirror the backend contract in
 * `src/domain/mediaRequests/contracts.ts`. Keep them in sync.
 *
 * The list is polled rather than streamed: requests are decided by people, so a
 * minute-scale refresh is ample. The cadence matters more than usual because a
 * box-wide read fans out to every user's request manager, so this hook polls at
 * the slow end and stops entirely while the tab is hidden — an admin who leaves
 * the page open all day costs nothing until they look at it.
 *
 * Filtering is deliberately NOT done here. The server returns the whole
 * normalized set for the requested scope, and the table filters it locally, so
 * typing in the search box never re-dials anyone's provider.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { getCsrfToken } from "../lib/csrf";

// ---------------------------------------------------------------------------
// Types (mirrored from backend src/domain/mediaRequests/contracts.ts)
// ---------------------------------------------------------------------------

export type MediaRequestProviderId = "seerr" | "ombi";

export type MediaRequestMediaType = "movie" | "tv";

export type MediaRequestStatus =
  | "pending"
  | "approved"
  | "declined"
  | "available"
  | "failed"
  /**
   * A request-history row: requested and no longer pending in the request
   * manager, but not confirmed present in the library. Never produced by a
   * provider — only by the stored request history.
   */
  | "fulfilled-unverified"
  | "unknown";

/**
 * The list's opening tab. Each value is a real status filter the list can honor
 * directly: `all` applies no status filter, `pending` is the actionable set,
 * `approved` is the approved set.
 */
export type MediaRequestDefaultView = "all" | "pending" | "approved";

/**
 * The account inside the request manager that made the request.
 *
 * Intentionally just an id and a name — providers return credential material on
 * their user objects, so the backend allowlists it away. Never widen this.
 */
export type MediaRequestRequester = {
  id: string | null;
  displayName: string | null;
};

/** What the provider behind a request is able to do. Read this, never guess from `providerId`. */
export type MediaRequestCapabilities = {
  /**
   * The provider stores a reason given when denying. When false, a reason is
   * accepted and then discarded, so asking someone to write one wastes it.
   */
  recordsDenyReason: boolean;
};

export type MediaRequest = {
  /** Unique across the whole box: provider request ids only differ within one instance. */
  key: string;
  providerId: MediaRequestProviderId;
  /** The QuickBox account whose request manager this came from. */
  ownerUsername: string;
  requestId: string;
  mediaType: MediaRequestMediaType;
  status: MediaRequestStatus;
  /** Absent on Seerr, which stores no title against a request. */
  title?: string;
  /**
   * Provider-relative artwork path — NOT a renderable URL.
   *
   * Resolving it needs a provider base, and remote artwork only reaches the
   * browser through the allowlisted image proxy and the opt-in artwork setting.
   * Until both are wired, nothing renders this.
   */
  posterPath?: string;
  tmdbId?: number;
  tvdbId?: number;
  imdbId?: string;
  is4k: boolean;
  requestedBy: MediaRequestRequester;
  requestedAt?: string;
  updatedAt?: string;
  deniedReason?: string;
  /**
   * Browser-reachable deep link into the source request app for this title,
   * built from the provider's configured linkback base. Absent when no base is
   * set or the provider is disabled — render nothing rather than a dead link.
   */
  linkbackUrl?: string;
  /** Authoritative — never infer approvability from `status`. */
  canApprove: boolean;
  canDeny: boolean;
  capabilities: MediaRequestCapabilities;
};

/** An instance that answered with a failure. */
export type MediaRequestError = {
  providerId: MediaRequestProviderId;
  ownerUsername: string;
  message: string;
};

/** An instance that is installed but not currently usable. */
export type MediaRequestProblem = {
  providerId: MediaRequestProviderId;
  ownerUsername: string;
  reason: "no_api_key" | "no_port" | "unreachable";
  detail: string;
};

/**
 * A per-instance count of fulfilled history rows that carry no identity — the
 * request manager purged their title/id, so they cannot be shown as cards.
 * Surfaced as list metadata so the list still accounts for them.
 */
export type MediaRequestHistoryNotice = {
  providerId: MediaRequestProviderId;
  ownerUsername: string;
  count: number;
  /** ISO timestamps bounding the aggregated rows, when known. */
  earliest?: string;
  latest?: string;
};

export type MediaRequestsData = {
  requests: MediaRequest[];
  errors: MediaRequestError[];
  problems: MediaRequestProblem[];
  /** Purged, identity-less history counts, per instance. */
  historyNotices: MediaRequestHistoryNotice[];
  /** True when the caller is seeing more than their own instances. */
  aggregated: boolean;
  /**
   * Whether "open in Media Portal" links should be offered for rows carrying a
   * `libraryMatch`. False when streaming is off or no media server is connected
   * — the portal target would not resolve, so the link is withheld.
   */
  portalLinksEnabled: boolean;
  /**
   * The admin-configured default tab and In-Library page size, carried on the
   * list itself so a viewer without settings access still opens on the
   * configured view and pages the In-Library set the configured way.
   */
  defaultView: MediaRequestDefaultView;
  inLibraryPageSize: number;
};

export type MediaRequestScope = "own" | "all";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * 60s. A box-wide read costs (users x providers x 1-2 upstream calls), so this
 * sits at the slow end of what the surface needs rather than the fast end of
 * what it could tolerate.
 */
const POLL_INTERVAL_MS = 60_000;

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

/** POST with a JSON body → both headers. */
const csrfHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = getCsrfToken();
  if (token) headers["x-csrf-token"] = token;
  return headers;
};

type Envelope<T> = { ok: true; data: T } | { ok: false; message: string };

/** Read a `{ ok, data | message }` envelope, normalizing transport failures. */
const readEnvelope = async <T>(res: Response): Promise<Envelope<T>> => {
  const json = (await res.json().catch(() => null)) as
    | { ok: true; data: T }
    | { ok: false; message?: string }
    | null;

  if (!res.ok || !json?.ok) {
    const message =
      (json && "message" in json && json.message) ||
      (res.status === 403
        ? "You do not have access to these request managers"
        : res.status === 404
          ? "Not found"
          : `Request failed (HTTP ${res.status})`);
    return { ok: false, message };
  }
  return { ok: true, data: json.data };
};

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export type MediaRequestActionTarget = Pick<
  MediaRequest,
  "providerId" | "ownerUsername" | "mediaType" | "requestId"
>;

export type MediaRequestActionResult = {
  ok: boolean;
  message: string;
  status?: MediaRequestStatus;
};

const postAction = async (
  path: "approve" | "deny",
  target: MediaRequestActionTarget,
  reason?: string,
): Promise<MediaRequestActionResult> => {
  try {
    const res = await fetch(`/api/media-requests/${path}`, {
      method: "POST",
      headers: csrfHeaders(),
      body: JSON.stringify({
        providerId: target.providerId,
        // The API names the instance owner `username`.
        username: target.ownerUsername,
        mediaType: target.mediaType,
        requestId: target.requestId,
        ...(reason ? { reason } : {}),
      }),
    });

    const envelope = await readEnvelope<{ message: string; status?: MediaRequestStatus }>(res);
    if (!envelope.ok) return { ok: false, message: envelope.message };
    return {
      ok: true,
      message: envelope.data.message,
      ...(envelope.data.status !== undefined && { status: envelope.data.status }),
    };
  } catch {
    return { ok: false, message: "Could not reach the server" };
  }
};

export const approveRequest = (target: MediaRequestActionTarget): Promise<MediaRequestActionResult> =>
  postAction("approve", target);

export const denyRequest = (
  target: MediaRequestActionTarget,
  reason?: string,
): Promise<MediaRequestActionResult> => postAction("deny", target, reason);

/** Fetch one request fresh from its provider. Returns null when it is gone. */
export const fetchRequestDetail = async (
  target: MediaRequestActionTarget,
): Promise<{ ok: true; request: MediaRequest } | { ok: false; message: string }> => {
  const params = new URLSearchParams({
    providerId: target.providerId,
    username: target.ownerUsername,
    mediaType: target.mediaType,
    requestId: target.requestId,
  });

  try {
    const res = await fetch(`/api/media-requests/detail?${params.toString()}`, {
      headers: { Accept: "application/json" },
    });
    const envelope = await readEnvelope<MediaRequest>(res);
    if (!envelope.ok) return { ok: false, message: envelope.message };
    return { ok: true, request: envelope.data };
  } catch {
    return { ok: false, message: "Could not reach the server" };
  }
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const EMPTY: MediaRequestsData = {
  requests: [],
  errors: [],
  problems: [],
  historyNotices: [],
  aggregated: false,
  portalLinksEnabled: false,
  // Mirrors the backend defaults until the first list envelope lands.
  defaultView: "all",
  inLibraryPageSize: 24,
};

/**
 * @param scope "all" spans every user's instances and is refused outright
 *   without the administrative grant — so only pass it for a caller that holds
 *   one. A refusal surfaces as an error, never as an empty list.
 */
export const useMediaRequests = (scope: MediaRequestScope, enabled: boolean = true) => {
  const [data, setData] = useState<MediaRequestsData>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  /**
   * @param showLoading true when a person is waiting on this (first load,
   *   scope change, the Refresh button) — it drives the loading state and is
   *   the only mode that surfaces an error. A poll stays silent: a blip that
   *   the next tick fixes is not worth an error banner over a good list.
   *
   * Never blanks the data on failure. On the very first load there is nothing
   * to blank, and later there is no reading of a stale list that is improved by
   * replacing it with an empty one.
   */
  const fetchRequests = useCallback(
    async (signal: AbortSignal, showLoading: boolean) => {
      if (showLoading) setLoading(true);

      try {
        const res = await fetch(`/api/media-requests?scope=${scope}`, {
          headers: { Accept: "application/json" },
          signal,
        });
        if (signal.aborted) return;

        const envelope = await readEnvelope<MediaRequestsData>(res);
        if (signal.aborted) return;

        if (!envelope.ok) {
          if (showLoading) setError(envelope.message);
          return;
        }

        setData(envelope.data);
        setError(null);
      } catch (err: unknown) {
        if (signal.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
        if (showLoading) setError("Could not reach the server");
      } finally {
        if (!signal.aborted && showLoading) setLoading(false);
      }
    },
    [scope],
  );

  // Foreground load. Also reruns on a scope change, which clears first: the
  // previous scope's requests are the wrong answer to the new question, and
  // leaving them on screen under an error would misrepresent whose they are.
  useEffect(() => {
    setData(EMPTY);
    setError(null);

    if (!enabled) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    void fetchRequests(controller.signal, true);
    return () => controller.abort();
  }, [enabled, fetchRequests]);

  // Background poll. Paused while the tab is hidden and caught up on re-show,
  // so a forgotten tab stops fanning out to everyone's request managers. Uses
  // its own controller so a tick can never cancel a foreground load and strand
  // its loading flag.
  useEffect(() => {
    if (!enabled) return;

    let pollController: AbortController | null = null;

    const tick = () => {
      if (typeof document !== "undefined" && document.hidden) return;
      pollController?.abort();
      pollController = new AbortController();
      void fetchRequests(pollController.signal, false);
    };

    const timer = window.setInterval(tick, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (typeof document !== "undefined" && !document.hidden) tick();
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisible);
    }

    return () => {
      window.clearInterval(timer);
      pollController?.abort();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisible);
      }
    };
  }, [enabled, fetchRequests]);

  /**
   * Re-read now — the Refresh button, and the catch-up after an approve/deny.
   *
   * Deliberately a foreground read: someone asked for this, so it shows that it
   * is working and says so if it fails. The table dims the current rows rather
   * than blanking them, so the list stays readable while it lands.
   */
  const refresh = useCallback(() => {
    if (!enabled) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    void fetchRequests(controller.signal, true);
  }, [enabled, fetchRequests]);

  return {
    requests: data.requests,
    errors: data.errors,
    problems: data.problems,
    historyNotices: data.historyNotices,
    aggregated: data.aggregated,
    portalLinksEnabled: data.portalLinksEnabled,
    defaultView: data.defaultView,
    inLibraryPageSize: data.inLibraryPageSize,
    loading,
    error,
    refresh,
  };
};
